/* Product selection uses the same Skio dynamic-box contract as the existing PDP. */
(() => {
  if (customElements.get('metta-v2-preview-marker')) return;
  customElements.define('metta-v2-preview-marker', class extends HTMLElement {});

  const compactUnits = text => String(text).replace(/(\d|\])[\s\u00a0\u202f]+(?=%|(?:kcal|kg|mg|g|kr|ml|cl)\b)/g, '$1');
  function compactContent(node) {
    const walker = document.createTreeWalker(node, NodeFilter.SHOW_TEXT);
    while (walker.nextNode()) {
      if (!walker.currentNode.parentElement?.closest('script,style,textarea')) walker.currentNode.textContent = compactUnits(walker.currentNode.textContent);
    }
  }
  async function mount(root) {
    if (root.dataset.v2Mounted) return;
    root.dataset.v2Mounted = 'loading';
    const strings = JSON.parse(root.querySelector('[data-v2-strings]').textContent);
    const copy = root.querySelector('[data-v2-copy]');
    if (copy) {
      try { Object.assign(strings, JSON.parse(copy.textContent)); } catch (error) { /* Keep locale fallbacks when editor content is incomplete. */ }
    }
    Object.keys(strings).forEach(key => { if (typeof strings[key] === 'string') strings[key] = compactUnits(strings[key]); });
    compactContent(root);
    const money = value => new Intl.NumberFormat(root.dataset.locale, { style: 'currency', currency: root.dataset.currency, maximumFractionDigits: value % 100 ? 2 : 0 }).formatToParts(value / 100).map(part => part.type === 'literal' ? part.value.replace(/\s/g, '') : part.value).join('');
    const find = selector => root.querySelector(selector);
    const all = selector => [...root.querySelectorAll(selector)];
    // Keep reading/tab order consistent with the visual sort, including unavailable flavors.
    all('[data-v2-flavors]').forEach(panel => [...panel.querySelectorAll('[data-v2-row]')]
      .sort((a, b) => Number(a.style.order) - Number(b.style.order)).forEach(row => panel.append(row)));
    const rows = all('[data-v2-row]');
    const quantities = new Map();
    const purchase = window.MettaV2Purchase;
    const R = window.MettaV2Rules;
    const commerce = JSON.parse(find('[data-v2-commerce-config]').textContent);
    const purchaseData = JSON.parse(find('[data-v2-purchase-config]').textContent);
    const catalogData = JSON.parse(find('[data-v2-catalog]').textContent);
    const oneBox = purchaseData.oneBox === true;
    let config, initialCart;
    if (oneBox) {
      all('[data-v2-delta], [data-v2-preview], [data-v2-jump]').forEach(button => { button.disabled = true; });
      try {
        const loaded = await window.MettaV2Skio.load({ ...catalogData.box, currency: root.dataset.currency });
        config = loaded.config;
        catalogData.box.config = config;
        purchaseData.config = config;
        find('[data-v2-purchase-config]').textContent = JSON.stringify(purchaseData);
        find('[data-v2-catalog]').textContent = JSON.stringify(catalogData);
        Object.assign(commerce, config.commerce, { enabled: true });
        commerce.eligibleProducts = catalogData.families.flatMap(f => f.products.filter(p => config.variantIds.includes(String(p.id))).map(p => String(p.productId)));
        const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, { cache: 'no-store', signal: AbortSignal.timeout(10000) });
        if (!response.ok) throw new Error('cart_request');
        initialCart = await response.json();
      } catch {
        root.dataset.v2Mounted = 'error';
        find('[data-v2-error-message]').textContent = strings.configurationError;
        find('[data-v2-purchase-error]').hidden = false;
        return;
      }
    } else config = { ...(purchaseData.config || {}), sizeRange: [purchaseData.minimumQuantity, purchaseData.config?.sizeRange?.[1]] };
    const commerceMode = R.mode(commerce, { previewRequested: new URL(location.href).searchParams.get('v2_rules') === 'preview' });
    const launchRules = commerceMode !== 'legacy';
    const preview = commerceMode === 'preview';
    root.dataset.rulesMode = commerceMode;
    root.dataset.oneBox = String(oneBox);
    find('[data-v2-rules-preview]').hidden = !preview;
    const currentUrl = new URL(location.href); currentUrl.searchParams.delete('v2_rules');
    find('[data-v2-rules-current]').href = currentUrl.href;
    let catalog;
    try { catalog = window.MettaV2Catalog.create(catalogData); } catch { /* Invalid editor data must not create a buyable selection. */ }
    const eligibleProducts = new Set(commerce.eligibleProducts.map(String));
    if (preview && !eligibleProducts.size) catalog?.products.forEach(product => eligibleProducts.add(product.productId));
    const plans = R.allowedPlans(purchase.plans(config), commerce);
    let selectedPlan = plans[0];
    let explicitPlanChoice = false;
    if (oneBox) {
      const existing = [...new Set((initialCart?.items || []).filter(item => item.properties?._dynamicBoxId === purchaseData.boxId)
        .map(item => purchase.id(item.selling_plan_allocation?.selling_plan?.id)).filter(Boolean))];
      if (existing.length === 1) selectedPlan = plans.find(p => purchase.id(p.gid) === existing[0]) || selectedPlan;
    }
    let busy = false;
    const rowData = row => {
      const product = catalog?.products.get(row.dataset.id);
      return {
        id: row.dataset.id, price: product?.price ?? Number(row.dataset.price),
        meals: product?.portionKind === 'meal' ? product.portionCount || 0 : 0,
        eligible: eligibleProducts.has(product?.productId),
        available: product?.available === true && row.dataset.available === 'true' && (!oneBox || config.variantIds.includes(row.dataset.id)), quantity: qty(row),
        allocations: product?.allocations || []
      };
    };
    const snapshot = (purchaseType = mode) => purchase.selection({ boxId: purchaseData.boxId, config, rows: rows.map(rowData), mode: purchaseType, planId: selectedPlan?.gid, boxIndex: 0, commerce: launchRules ? commerce : null, currency: root.dataset.currency });
    all('[data-v2-frequency]').forEach(select => {
      select.replaceChildren(...plans.map(plan => {
        const option = document.createElement('option');
        option.value = String(plan.intervalCount);
        option.textContent = (strings.intervalChoice || strings.intervalWeeks).replace('[count]', plan.intervalCount);
        return option;
      }));
      select.value = String(selectedPlan?.intervalCount || '');
      select.disabled = !plans.length;
    });
    let family = root.dataset.initialFamily;
    let foodshakeFamily = ['original', 'protein'].includes(family) ? family : 'original';
    let mode = 'subscription';
    const threshold = Number(root.dataset.shippingThreshold);
    const shippingFee = Number(root.dataset.shippingFee);
    const swedishShipping = root.dataset.shippingCountry === 'SE' && root.dataset.currency === 'SEK';
    const dialog = find('[data-v2-dialog]');
    const dialogContent = find('[data-v2-dialog-content]');
    const price = (row, discount = 0) => purchase.unitPrice(rowData(row), mode, selectedPlan, launchRules && !rowData(row).eligible ? 0 : discount);
    const qty = row => quantities.get(row.dataset.id) || 0;
    let attempt = 0;
    const measure = (name, extra = {}) => {
      try { window.MettaMeasurement?.track(name, { family, purchase_type: mode, weeks: mode === 'subscription' ? selectedPlan?.intervalCount : 0, bag_count: [...quantities.values()].reduce((sum, value) => sum + value, 0), ...extra }, root); } catch { /* Measurement cannot interrupt purchases. */ }
    };
    let lastFocus;
    function openDialog(content, drawer = false) {
      lastFocus = document.activeElement;
      compactContent(content);
      dialogContent.replaceChildren(content);
      dialog.classList.toggle('v2-dialog--drawer', drawer);
      dialog.setAttribute('aria-label', content.querySelector('h2')?.textContent || strings.previewTitle);
      dialog.showModal();
      dialog.scrollTop = 0;
    }
    function showError(message) {
      const error = find('[data-v2-purchase-error]');
      find('[data-v2-error-message]').textContent = message || '';
      error.hidden = !message;
    }
    function frequencyChoice(existing) {
      return new Promise(resolve => {
        const content = document.createElement('div');
        const heading = document.createElement('h2'); heading.textContent = strings.frequencyConflict;
        const description = document.createElement('p'); description.textContent = strings.frequencyConflictBody;
        content.append(heading, description);
        const finish = value => { dialog.removeEventListener('close', cancelled); dialog.close(); resolve(value); };
        const cancelled = () => resolve(null);
        dialog.addEventListener('close', cancelled, { once: true });
        for (const plan of plans.filter(p => existing.includes(purchase.id(p.gid)))) {
          const button = document.createElement('button'); button.type = 'button'; button.className = 'v2-button';
          button.textContent = strings.frequencyUseExisting.replace('[count]', plan.intervalCount);
          button.addEventListener('click', () => finish(purchase.id(plan.gid))); content.append(button);
        }
        const separate = document.createElement('button'); separate.type = 'button'; separate.className = 'v2-button';
        separate.textContent = strings.frequencySeparate.replace('[count]', selectedPlan.intervalCount);
        separate.addEventListener('click', () => finish('separate')); content.append(separate);
        openDialog(content);
      });
    }
    async function addToCart() {
      if (busy || preview || (launchRules && !eligibleProducts.size)) return;
      let order = snapshot();
      if (order.error) { update(); return; }
      busy = true;
      showError('');
      update();
      const route = window.Shopify?.routes?.root || '/';
      let accepted = false;
      let stage = 'box_validation';
      const currentAttempt = ++attempt;
      measure('cart_add_started', { attempt: currentAttempt });
      try {
        // Revalidate before every add; never silently purchase with stale box rules.
        let boxResponse;
        if (oneBox) {
          const loaded = await window.MettaV2Skio.load({ ...catalog.box, currency: root.dataset.currency }, { fresh: true });
          if (JSON.stringify(loaded.config) !== JSON.stringify(config)) {
            showError(strings.configurationError); return;
          }
          boxResponse = loaded.raw;
          const cartResponse = await fetch(`${route}cart.js`, { cache: 'no-store', signal: AbortSignal.timeout(10000) });
          if (!cartResponse.ok) throw new Error('cart_request');
          const cart = await cartResponse.json();
          const existing = [...new Set(cart.items.filter(item => item.properties?._dynamicBoxId === purchaseData.boxId)
            .map(item => purchase.id(item.selling_plan_allocation?.selling_plan?.id)).filter(Boolean))];
          if (mode === 'subscription' && existing.length && !existing.includes(purchase.id(selectedPlan?.gid))) {
            const inherited = !explicitPlanChoice && existing.length === 1 && plans.some(p => purchase.id(p.gid) === existing[0]);
            const decision = inherited ? existing[0] : await frequencyChoice(existing);
            if (!decision) return;
            if (decision !== 'separate') {
              selectedPlan = plans.find(p => purchase.id(p.gid) === decision);
              all('[data-v2-frequency]').forEach(select => { select.value = String(selectedPlan.intervalCount); });
              order = snapshot();
              if (order.error) return;
            }
          }
        } else {
        const boxRequest = await fetch('https://api.skio.com/storefront-http/get-dynamic-box', {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ productPlatformId: catalog?.box.parentProductId }),
          signal: AbortSignal.timeout(10000)
        });
        if (!boxRequest.ok) throw new Error(strings.purchaseError);
        boxResponse = await boxRequest.json();
        }
        const checked = catalog?.selection(quantities, { mode, weeks: selectedPlan?.intervalCount, boxResponse });
        if (!checked || checked.errors.length || checked.rows.some(({ product }) => mode === 'subscription' && checked.plan.variantPlanIds[product.id] !== purchase.id(selectedPlan?.gid))) {
          showError(strings.configurationError);
          measure('cart_add_failed', { attempt: currentAttempt, stage: 'box_validation' });
          return;
        }
        stage = 'cart_response';
        const response = await fetch(`${route}cart/add.js`, {
          method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: order.items }), signal: AbortSignal.timeout(20000)
        });
        const result = await response.json();
        if (!response.ok) throw new Error(result.description || strings.purchaseError);
        accepted = true;
        measure('cart_add_succeeded', { attempt: currentAttempt, line_count: order.items.length });
        quantities.clear();
        explicitPlanChoice = false;
        // The existing theme owns rendering, opening and validating the drawer.
        if (window.MettaCart) await window.MettaCart.open();
        else if (typeof window.cart?.getCart === 'function') await window.cart.getCart();
        else window.location.assign(`${route}cart`);
      } catch (error) {
        // Never repeat the POST automatically: a lost response can still mean a successful add.
        if (accepted) window.location.assign(`${route}cart`);
        else { showError(strings.purchaseError); measure('cart_add_failed', { attempt: currentAttempt, stage }); }
      } finally {
        busy = false;
        update();
      }
    }
    find('[data-v2-close]').addEventListener('click', () => dialog.close());
    dialog.addEventListener('click', event => { if (event.target === dialog) { const b = dialog.getBoundingClientRect(); if (event.clientX < b.left || event.clientX > b.right || event.clientY < b.top || event.clientY > b.bottom) dialog.close(); } });
    dialog.addEventListener('close', () => { dialogContent.querySelectorAll('video').forEach(video => video.pause()); lastFocus?.focus(); });

    // Skio's price model (priceRange.discountBase) decides what the threshold counts.
    // Since 2026-10-13 it is price_after_selling_plan: what the customer pays, so the
    // ratio is 1. If the box counts the price before the subscription discount, the
    // remaining amount and milestone are converted into paid kronor with the selection's
    // paid/ordinary ratio. The threshold itself always comes from the live Skio box.
    function volumeView(order) {
      const quote = order.quote;
      if (!(swedishShipping && launchRules && eligibleProducts.size > 0 && quote && !quote.error && commerce.percent > 0)) return { available: false };
      let ratio = 1;
      if (commerce.discountBase === 'price_before_selling_plan') {
        if (quote.eligibleSubtotal > 0) ratio = quote.paidEligibleSubtotal / quote.eligibleSubtotal;
        else {
          const current = rows.find(row => row.dataset.family === family);
          const full = Number(current?.dataset.price);
          const paid = current ? purchase.unitPrice(rowData(current), mode, selectedPlan) : null;
          if (full > 0 && paid != null) ratio = paid / full;
        }
      }
      const whole = value => Math.ceil(value / 100) * 100;
      const reached = quote.discountAmount > 0;
      return { available: true, percent: commerce.percent, reached, saving: quote.discountAmount || 0,
        goal: whole(commerce.threshold * ratio), remaining: reached ? 0 : whole(quote.remaining * ratio) };
    }

    function updateSticky(order, fee) {
      const { count, total, quote } = order;
      const button = find('[data-v2-jump]');
      const currentRow = rows.find(row => row.dataset.family === family);
      const fromPrice = currentRow ? purchase.unitPrice(rowData(currentRow), mode, selectedPlan) : null;
      find('[data-v2-sticky-price]').textContent = total === null ? '—' : count ? money(total) : fromPrice != null ? `${strings.from} ${money(fromPrice)}` : strings.choose;
      find('[data-v2-sticky-caption]').textContent = count ? `${count} ${count === 1 ? strings.bag : strings.bags}` : fromPrice != null ? strings.perBag : strings.emptyHint;
      button.textContent = busy ? strings.adding : !count
        ? button.dataset.emptyLabel
        : preview ? strings.rulesPreviewButton : strings.stickyAdd;
      button.disabled = busy || (!!count && (preview || !!order.error || !catalog || (launchRules && !eligibleProducts.size)));
      button.setAttribute('aria-busy', String(busy));
      const rewards = find('[data-v2-sticky-rewards]');
      const shippingLabel = find('[data-v2-sticky-shipping]');
      const rewardLabel = find('[data-v2-sticky-reward]');
      const progress = find('[data-v2-sticky-progress]');
      // The new volume offer is visible only in the existing launch/preview mode.
      // Do not promise it while Skio's legacy quantity tiers still price the order.
      const volumeAvailable = swedishShipping && launchRules && eligibleProducts.size > 0 && !quote?.error && !!quote && commerce.percent > 0;
      const free = count > 0 && total !== null && fee === 0;
      const discounted = volumeAvailable && quote.discountAmount > 0;
      const complete = free && discounted;
      rewards.dataset.state = complete ? 'complete' : free ? 'shipping-earned' : 'shipping';
      const percent = String(commerce.percent);
      shippingLabel.textContent = complete ? strings.stickyBothUnlocked.replace('[percent]', percent)
        : free ? strings.stickyFree
        : !swedishShipping || total === null ? strings.shippingCheckout
        : count ? strings.stickyShippingRemaining.replace('[amount]', money(Math.max(0, threshold - total))) : strings.freeThreshold;
      rewardLabel.hidden = !volumeAvailable || complete || !count;
      rewardLabel.textContent = volumeAvailable ? (discounted ? strings.stickyVolumeApplied : strings.stickyVolumeRemaining)
        .replace('[percent]', percent).replace('[amount]', money(volumeView(order).remaining || 0)) : '';
      progress.hidden = !swedishShipping || total === null || complete || (free && !volumeAvailable);
      // Thin edge bar on one paid axis, like the box under the button: notch at free shipping, end at the extra discount.
      const view = volumeView(order);
      const shipGoal = (launchRules ? commerce.shipping?.threshold : threshold) || 0;
      const max = Math.max(shipGoal, view.available ? view.goal : 0) || 1;
      const paid = count ? (quote ? quote.subtotal : total) ?? 0 : 0;
      find('[data-v2-sticky-fill]').style.width = `${Math.min(100, Math.max(0, paid / max * 100))}%`;
      const notch = find('[data-v2-sticky-notch]');
      if (notch) { notch.hidden = !view.available || shipGoal >= max; notch.style.left = `${shipGoal / max * 100}%`; }
    }

    function update() {
      const focusedSelection = document.activeElement?.closest('[data-v2-selected-action]');
      const selectedFocus = focusedSelection ? { id: focusedSelection.dataset.variant, action: focusedSelection.dataset.v2SelectedAction } : null;
      const selected = rows.filter(row => qty(row) > 0);
      const order = snapshot();
      const { count, meals, discount } = order;
      const total = order.total ?? 0;
      const proteinPortions = selected.reduce((sum, row) => {
        const product = catalog?.products.get(row.dataset.id);
        return sum + (product?.portionKind === 'protein_portion' ? (product.portionCount || 0) * qty(row) : 0);
      }, 0);
      const portionParts = [meals ? `${meals} ${strings.meals}` : '', proteinPortions ? `${proteinPortions} ${strings.proteinPortions}` : ''].filter(Boolean);
      const hasUnknownPortions = selected.some(row => !catalog?.products.get(row.dataset.id)?.portionCount);
      const current = rows.find(row => row.dataset.family === family);
      rows.forEach(row => {
        row.querySelector('output').textContent = qty(row);
        row.querySelector('[data-v2-delta="-1"]').hidden = qty(row) === 0;
        const label = row.querySelector('[data-v2-row-price]'); if (label) label.textContent = price(row, discount) === null ? strings.unavailable : money(price(row, discount));
        const available = rowData(row).available && (mode === 'onetime' || purchase.unitPrice(rowData(row), mode, selectedPlan) !== null);
        row.querySelector('[data-v2-delta="1"]').disabled = busy || !available || count >= order.maximum;
        row.querySelector('[data-v2-delta="-1"]').disabled = busy;
      });
      const setCopy = (selector, text, visible = true) => { const node = find(selector); if (node) { node.textContent = text; node.hidden = !visible; } };
      const setText = (selector, text) => { const node = find(selector); if (node) node.textContent = text; };
      if (current) {
        const product = catalog?.products.get(current.dataset.id);
        const portions = product?.portionCount || 1;
        const optionUnit = product?.portionCount ? (product.portionKind === 'meal' ? strings.perMealLabel : strings.perPortion) : strings.perBag;
        const subscriptionPrice = purchase.unitPrice(rowData(current), 'subscription', selectedPlan);
        setText('[data-v2-option-price]', subscriptionPrice === null ? strings.unavailable : `${money(subscriptionPrice)} / ${strings.bag}`);
        setText('[data-v2-onetime-price]', `${money(Number(current.dataset.price))} / ${strings.bag}`);
        setCopy('[data-v2-sub-unit]', subscriptionPrice === null ? strings.unavailable : money(Math.round(subscriptionPrice / portions)));
        setCopy('[data-v2-sub-compare]', money(Math.round(Number(current.dataset.price) / portions)), subscriptionPrice !== null && subscriptionPrice < Number(current.dataset.price));
        setCopy('[data-v2-option-unit]', optionUnit);
        setCopy('[data-v2-onetime-detail]', `${money(Math.round(Number(current.dataset.price) / portions))} ${optionUnit} · ${strings.ordinaryPrice}`);
        const saving = Math.round((1 - (purchase.unitPrice(rowData(current), 'subscription', selectedPlan) ?? Number(current.dataset.price)) / Number(current.dataset.price)) * 100);
        setText('[data-v2-saving]', saving > 0 ? strings.savingLabel.replace('[percent]', saving) : '');
      } else {
        setText('[data-v2-option-price]', strings.unconfiguredCategory);
        setText('[data-v2-onetime-price]', '');
        setText('[data-v2-saving]', '');
        setCopy('[data-v2-sub-unit]', '—');
        setCopy('[data-v2-sub-compare]', '', false);
        setCopy('[data-v2-option-unit]', '');
        setCopy('[data-v2-onetime-detail]', '');
      }
      const currentProduct = current && catalog?.products.get(current.dataset.id);
      let unitLabel = strings.perBag, unitPrice = count ? money(Math.round(total / count)) : current && price(current) !== null ? money(price(current)) : '—';
      if (count && !hasUnknownPortions && (meals === 0 || proteinPortions === 0)) {
        const portions = meals || proteinPortions;
        if (portions) { unitPrice = money(Math.round(total / portions)); unitLabel = meals ? strings.perMealLabel : strings.perPortion; }
      } else if (!count && currentProduct?.portionCount && price(current) !== null) {
        unitPrice = money(Math.round(price(current) / currentProduct.portionCount));
        unitLabel = currentProduct.portionKind === 'meal' ? strings.perMealLabel : strings.perPortion;
      }
      let mealSubtotal = 0, proteinSubtotal = 0;
      for (const row of selected) {
        const product = catalog?.products.get(row.dataset.id);
        const line = (price(row, discount) || 0) * qty(row);
        if (product?.portionKind === 'meal') mealSubtotal += line;
        else if (product?.portionKind === 'protein_portion') proteinSubtotal += line;
      }
      const mixedPortions = meals > 0 && proteinPortions > 0 && !hasUnknownPortions;
      if (mixedPortions) { unitPrice = money(Math.round(mealSubtotal / meals)); unitLabel = strings.perMealLabel; }
      setCopy('[data-v2-secondary-unit]', mixedPortions ? (strings.secondaryProteinPrice || '').replace('[amount]', money(Math.round(proteinSubtotal / proteinPortions))) : '', mixedPortions && !order.quote?.error);
      if (order.quote?.error) unitPrice = '—';
      find('[data-v2-per-meal]').textContent = unitPrice;
      find('[data-v2-unit-label]').textContent = unitLabel;
      find('[data-v2-total-row]').hidden = !count;
      find('[data-v2-total-label]').textContent = strings.total;
      find('[data-v2-total]').textContent = order.total === null ? '—' : money(total);
      find('[data-v2-meal-count]').hidden = !portionParts.length;
      find('[data-v2-selected]').hidden = !count;
      find('[data-v2-meal-count]').textContent = portionParts.join(' · ');
      find('[data-v2-empty-hint]').hidden = count > 0;
      const reference = count && mode === 'subscription' ? snapshot('onetime') : null;
      const referenceTotal = reference?.total;
      const savings = referenceTotal != null && order.total != null ? Math.max(0, referenceTotal - total) : 0;
      const divisor = count && !hasUnknownPortions && (meals === 0 || proteinPortions === 0) && (meals || proteinPortions) ? meals || proteinPortions : count;
      const comparableUnit = mixedPortions ? selected.filter(row => catalog?.products.get(row.dataset.id)?.portionKind === 'meal').reduce((sum, row) => sum + (purchase.unitPrice(rowData(row), 'onetime', selectedPlan, reference?.discount || 0) || 0) * qty(row), 0) / meals : divisor ? referenceTotal / divisor : 0;
      setCopy('[data-v2-compare-unit]', savings && divisor ? money(Math.round(comparableUnit)) : '', savings > 0);
      setCopy('[data-v2-bag-total]', `${portionParts.length ? '· ' : ''}${count} ${count === 1 ? strings.bag : strings.bags} · ${order.total === null ? '—' : money(total)}`);
      const countRow = find('[data-v2-count-row]'); if (countRow) countRow.hidden = !count;
      setCopy('[data-v2-summary-saving]', savings ? (strings.summarySaving || '').replace('[amount]', money(savings)).replace('[percent]', Math.round(savings / referenceTotal * 100)) : '', savings > 0);
      const selectionDetails = find('[data-v2-selection-details]');
      if (selectionDetails) selectionDetails.hidden = new Set(selected.map(row => row.dataset.family)).size < 2 && !selected.some(row => row.dataset.family !== family);
      setText('[data-v2-order-type]', mode === 'subscription' ? strings.subscription : strings.onetime);
      const summaryFrequency = find('[data-v2-summary-frequency]'); if (summaryFrequency) summaryFrequency.hidden = mode !== 'subscription';
      const rates = launchRules ? R.shipping(order.total, commerce, { country: root.dataset.shippingCountry, currency: root.dataset.currency }) : null;
      const fee = launchRules ? rates?.dhl : swedishShipping ? (total >= threshold ? 0 : shippingFee) : null;
      setText('[data-v2-shipping-text]', !swedishShipping ? strings.shippingCheckout : count ? fee == null ? strings.shippingCheckout : fee === 0 ? (strings.freeShort || strings.free) : strings.shippingCost.replace('[amount]', money(fee)) : strings.freeThreshold);
      // Plan cards (Huel layout): each card shows its own total and unit price for the current box,
      // or the per-bag price of the active product line before anything is chosen.
      if (find('[data-v2-plans]')) {
        const planProduct = current && catalog?.products.get(current.dataset.id);
        const planLine = planMode => {
          if (count) {
            const result = planMode === mode ? order : snapshot(planMode);
            if (result.total == null || order.quote?.error) return null;
            const units = hasUnknownPortions ? count : result.meals || proteinPortions || count;
            const label = hasUnknownPortions ? strings.perBag : result.meals ? strings.perMealLabel : proteinPortions ? strings.perPortion : strings.perBag;
            return { total: result.total, unit: Math.round(result.total / units), label };
          }
          if (!current) return null;
          const bag = planMode === 'subscription' ? purchase.unitPrice(rowData(current), 'subscription', selectedPlan) : Number(current.dataset.price);
          if (bag == null) return null;
          const units = planProduct?.portionCount || 1;
          const label = planProduct?.portionCount ? (planProduct.portionKind === 'meal' ? strings.perMealLabel : strings.perPortion) : strings.perBag;
          return { total: bag, unit: Math.round(bag / units), label };
        };
        const planText = line => line ? `${money(line.total)} | ${money(line.unit)} ${line.label}` : strings.unavailable;
        const subLine = planLine('subscription'), oneLine = planLine('onetime');
        const portionsText = count ? portionParts.join(' · ') : planProduct?.portionCount ? `${planProduct.portionCount} ${planProduct.portionKind === 'meal' ? strings.meals : strings.proteinPortions}` : '';
        all('[data-v2-plan-portions]').forEach(node => { node.textContent = portionsText; });
        setText('[data-v2-plan-price="subscription"]', planText(subLine));
        setText('[data-v2-plan-price="onetime"]', planText(oneLine));
        const cheaper = !!subLine && !!oneLine && subLine.total < oneLine.total;
        setCopy('[data-v2-plan-compare]', cheaper ? planText(oneLine) : '', cheaper);
        const planSaving = cheaper ? Math.round((1 - subLine.total / oneLine.total) * 100) : 0;
        const savingNode = find('[data-v2-plan-saving]');
        if (savingNode) { savingNode.textContent = (strings.planSaving || '').replace('[percent]', planSaving); savingNode.closest('li').hidden = planSaving <= 0; }
        const planShipping = find('[data-v2-plan-shipping]');
        if (planShipping) { planShipping.textContent = count && fee === 0 ? (strings.freeShort || strings.free) : strings.freeThreshold; planShipping.closest('li').hidden = !swedishShipping; }
      }
      // Progress box under the button: delivery day (own script), free shipping and extra discount on one axis.
      const progress = find('[data-v2-progress]');
      if (progress) {
        const pick = selector => progress.querySelector(selector);
        const shipGoal = (launchRules ? commerce.shipping?.threshold : threshold) || 0;
        const volume = volumeView(order);
        const shipDone = count > 0 && fee === 0;
        const percent = String(volume.percent ?? '');
        const volumeText = key => (strings[key] || '').replace('[amount]', money(volume.remaining)).replace('[percent]', percent);
        let primary = '', secondary = '', state;
        if (!count) {
          state = 'empty';
          secondary = volume.available ? `${strings.freeThreshold} · ${(strings.progressVolumeFrom || '').replace('[percent]', percent).replace('[amount]', money(volume.goal))}` : strings.freeThreshold;
        } else if (!shipDone) {
          state = 'shipping';
          primary = (strings.progressShippingRemaining || '').replace('[amount]', money(Math.max(0, shipGoal - (order.total ?? 0))));
          secondary = volume.available && !volume.reached ? `· ${volumeText('progressVolumeNext')}` : '';
        } else if (volume.available && volume.reached) {
          state = 'complete';
          primary = (strings.progressBothDone || '').replace('[percent]', percent);
          secondary = (strings.progressSaving || '').replace('[amount]', money(volume.saving));
        } else {
          state = volume.available ? 'volume' : 'shipping-done';
          primary = strings.progressShippingDone || '';
          secondary = volume.available ? `· ${volumeText('progressVolumeRemaining')}` : '';
        }
        progress.dataset.state = state;
        progress.dataset.volume = String(!!volume.available);
        pick('[data-v2-progress-goals]').hidden = !swedishShipping;
        pick('[data-v2-progress-primary]').textContent = primary;
        pick('[data-v2-progress-secondary]').textContent = secondary;
        const max = Math.max(shipGoal, volume.available ? volume.goal : 0) || 1;
        const paid = count ? (order.quote ? order.quote.subtotal : order.total) ?? 0 : 0;
        const position = value => `${Math.min(100, Math.max(0, value / max * 100))}%`;
        pick('[data-v2-progress-fill]').style.width = state === 'complete' ? '100%' : position(paid);
        const shipMarker = pick('[data-v2-progress-marker="shipping"]');
        shipMarker.style.left = position(shipGoal); shipMarker.classList.toggle('is-reached', shipDone);
        const shipLabel = pick('[data-v2-progress-label="shipping"]');
        shipLabel.style.left = position(shipGoal); shipLabel.classList.toggle('is-reached', shipDone);
        pick('[data-v2-progress-amount="shipping"]').textContent = money(shipGoal);
        const volumeMarker = pick('[data-v2-progress-marker="volume"]');
        const volumeLabel = pick('[data-v2-progress-label="volume"]');
        volumeMarker.hidden = volumeLabel.hidden = !volume.available;
        volumeMarker.style.left = position(volume.goal || 0);
        volumeMarker.classList.toggle('is-reached', !!volume.reached);
        volumeLabel.classList.toggle('is-reached', !!volume.reached);
        pick('[data-v2-progress-amount="volume"]').textContent = volume.available ? money(volume.goal) : '';
        pick('[data-v2-progress-volume-name]').textContent = volume.available ? (strings.progressVolumeLabel || '').replace('[percent]', percent) : '';
      }
      const volumeNote = find('[data-v2-volume-note]');
      volumeNote.hidden = !launchRules || !count || !!order.quote?.error || !eligibleProducts.size;
      volumeNote.textContent = order.quote?.discountAmount > 0 ? strings.volumeApplied.replace('[percent]', commerce.percent).replace('[amount]', money(order.quote.discountAmount)) : strings.volumeRemaining.replace('[percent]', commerce.percent).replace('[amount]', money(volumeView(order).remaining || 0));
      find('[data-v2-shipping-text]')?.classList.toggle('is-filled', count > 0);
      find('[data-v2-preview]').disabled = busy || preview || !!order.error || !catalog || (launchRules && !eligibleProducts.size);
      find('[data-v2-preview]').setAttribute('aria-busy', String(busy));
      find('[data-v2-preview]').textContent = preview ? strings.rulesPreviewButton : busy ? strings.adding : !count ? strings.empty : order.error === 'minimum' ? strings.minimum.replace('[count]', order.minimum) : (strings.addSelection || strings.add).replace('[count]', count).replace('[bags]', count === 1 ? strings.bag : strings.bags).replace('[amount]', money(total));
      all('.v2-purchase-options input, [data-v2-frequency]').forEach(control => { control.disabled = busy || (control.tagName === 'SELECT' && !plans.length); });
      if (launchRules && !eligibleProducts.size) showError(strings.configurationError);
      else if (order.error && !['minimum', 'maximum'].includes(order.error)) showError(strings.configurationError);
      else if (order.error === 'maximum') showError(strings.maximum.replace('[count]', order.maximum));
      find('[data-v2-upsell]').hidden = !count || root.dataset.showProductCategories !== 'true';
      updateSticky(order, fee);

      const selectedNodes = selected.map(row => {
        const line = document.createElement('div'); line.className = 'v2-selected-row';
        const title = document.createElement('button'); title.type = 'button'; title.className = 'v2-selected-title'; title.textContent = row.dataset.title;
        title.addEventListener('click', () => {
          activateFamily(row.dataset.family, true);
          row.querySelector('[data-v2-info]').focus({ preventScroll: true });
          row.scrollIntoView({ block: 'nearest', behavior: 'instant' });
        });
        const controls = document.createElement('div'); controls.className = 'v2-quantity';
        for (const action of [-1, 1]) {
          const button = document.createElement('button'); button.type = 'button';
          button.textContent = action === 1 ? '+' : '−'; button.dataset.v2SelectedAction = String(action); button.dataset.variant = row.dataset.id;
          button.setAttribute('aria-label', `${action === 1 ? strings.increase : strings.decrease} ${row.dataset.title}`);
          button.disabled = busy || row.querySelector(`[data-v2-delta="${action}"]`).disabled;
          button.addEventListener('click', () => row.querySelector(`[data-v2-delta="${action}"]`).click());
          if (action === 1) { const count = document.createElement('output'); count.textContent = qty(row); controls.append(count); }
          controls.append(button);
        }
        line.append(title, controls); return line;
      });
      find('[data-v2-selected]').replaceChildren(...selectedNodes);
      if (selectedFocus && !busy) {
        const restored = find(`[data-v2-selected-action="${selectedFocus.action}"][data-variant="${CSS.escape(selectedFocus.id)}"]`);
        ((restored && !restored.disabled ? restored : find(`[data-v2-selected-action="-1"][data-variant="${CSS.escape(selectedFocus.id)}"]`)) || find('[data-v2-selected-action]:not(:disabled)') || find('[data-v2-flavor-panel] [role="tab"][aria-selected="true"]'))?.focus({ preventScroll: true });
      }
    }
    rows.forEach(row => {
      row.querySelectorAll('[data-v2-delta]').forEach(button => button.addEventListener('click', () => {
        if (busy) return;
        showError('');
        quantities.set(row.dataset.id, Math.max(0, qty(row) + Number(button.dataset.v2Delta))); update();
        measure('quantity_changed', { variant_id: row.dataset.id, quantity: qty(row) });
      }));
      row.querySelector('[data-v2-info]').addEventListener('click', () => {
        // Reuse the live flavor content, within a native modal for focus management.
        const content = row.querySelector('template').content.querySelector('.popup-info-content').cloneNode(true);
        content.querySelectorAll('[data-accordion-toggle]').forEach((toggle, i) => {
          const accordion = toggle.closest('[data-accordion]');
          const body = accordion.querySelector('.popup-info-accordion__body');
          body.id = `v2-info-${root.id || root.dataset.initialFamily}-${row.dataset.id}-${i}`;
          toggle.setAttribute('aria-controls', body.id);
          toggle.setAttribute('aria-expanded', String(accordion.classList.contains('is-expanded')));
          toggle.addEventListener('click', () => {
            const expanded = accordion.classList.toggle('is-expanded');
            toggle.setAttribute('aria-expanded', String(expanded));
          });
        });
        openDialog(content, true);
        measure('flavor_opened', { variant_id: row.dataset.id });
      });
    });
    function categoryFor(value) { return value === 'upsell' ? 'protein_powder' : value === 'oatmeal' ? 'oatmeal' : 'foodshake'; }
    // Sections under the buy module describe the page's own product line. When the visitor switches to another
    // line (e.g. Oatmeal on the Food Shake page) they are hidden so the page never shows the wrong product's info;
    // reviews, guarantee and contact stay. Only sections hidden here are restored when switching back.
    const pageCategory = categoryFor(root.dataset.initialFamily);
    const keepSection = section => /review|satisfaction|guarantee|contact|klaviyo/i.test(section.id) || !!section.querySelector('.v2-contact, [class*="klaviyo"], [class*="reviews-marquee"]');
    function syncPageSections(category) {
      const host = root.closest('.shopify-section');
      if (!host) return;
      const other = category !== pageCategory;
      for (let section = host.nextElementSibling; section; section = section.nextElementSibling) {
        if (!section.classList.contains('shopify-section') || keepSection(section)) continue;
        if (other && !section.hidden) { section.hidden = true; section.dataset.v2HiddenByCategory = 'true'; }
        else if (!other && section.dataset.v2HiddenByCategory) { section.hidden = false; delete section.dataset.v2HiddenByCategory; }
      }
      all('[data-v2-story-more]').forEach(link => { link.hidden = !other; });
    }
    function familyFromUrl() {
      const url = new URL(location.href);
      const requested = url.searchParams.get('family');
      const category = url.searchParams.get('category');
      if (catalog?.families.has(requested) && (!category || categoryFor(requested) === category)) return requested;
      if (category === 'oatmeal') return 'oatmeal';
      if (category === 'protein_powder') return 'upsell';
      if (category === 'foodshake') {
        const landing = catalog?.landingFamily(url.href) || root.dataset.initialFamily;
        return ['original', 'protein'].includes(landing) ? landing : 'original';
      }
      return catalog?.landingFamily(url.href) || root.dataset.initialFamily;
    }
    function activateFamily(next, push = false) {
      if (!catalog?.families.has(next)) return;
      const previous = root.dataset.activeFamily;
      family = next;
      if (['original', 'protein'].includes(family)) foodshakeFamily = family;
      const category = categoryFor(family);
      root.dataset.activeFamily = family;
      root.dataset.activeCategory = category;
      all('[data-v2-family], [data-v2-category]').forEach(tab => {
        const active = tab.dataset.v2Category ? tab.dataset.v2Category === category : tab.dataset.v2Family === foodshakeFamily;
        tab.setAttribute('aria-pressed', String(active));
        if (tab.getAttribute('role') === 'tab') { tab.setAttribute('aria-selected', String(active)); tab.tabIndex = active ? 0 : -1; }
      });
      all('[data-v2-foodshake-tabs]').forEach(tabs => { tabs.hidden = category !== 'foodshake'; });
      syncPageSections(category);
      all('[data-v2-title]').forEach(title => { title.hidden = title.dataset.v2Title !== family; });
      all('[data-v2-subtitle]').forEach(title => { title.hidden = title.dataset.v2Subtitle !== family; });
      all('[data-v2-gallery]').forEach(gallery => {
        gallery.hidden = gallery.dataset.v2Gallery !== family;
        gallery.querySelectorAll('video').forEach(video => video.pause());
        if (!gallery.hidden) {
          const template = gallery.querySelector('[data-v2-lazy-gallery]');
          if (template) { gallery.append(template.content.cloneNode(true)); template.remove(); }
          initGallery(gallery);
        }
      });
      all('[data-v2-flavors]').forEach(group => { group.hidden = group.dataset.v2Flavors !== family; });
      if (push) {
        const url = new URL(location.href);
        url.searchParams.set('category', category); url.searchParams.set('family', family);
        if (url.href !== location.href) history.pushState(history.state, '', url);
      }
      showError('');
      const preparation = find('[data-v2-preparation-copy]');
      if (preparation) preparation.textContent = family === 'oatmeal' ? strings.preparationOatmeal : family === 'upsell' ? strings.preparationProteinPowder : family === 'protein' ? strings.preparationProtein : strings.preparationFoodshake;
      all('[data-v2-preparation]').forEach(list => { list.hidden = list.dataset.v2Preparation !== family; });
      update();
      root.dispatchEvent(new CustomEvent('v2:family-change', { bubbles: true, detail: { family } }));
      if (previous && previous !== family) measure('category_changed', { previous_family: previous, source: push ? 'selection' : 'history' });
    }
    all('[data-v2-family]').forEach(button => button.addEventListener('click', () => activateFamily(button.dataset.v2Family, true)));
    all('[data-v2-category]').forEach(button => button.addEventListener('click', () => activateFamily(button.dataset.v2Category === 'foodshake' ? foodshakeFamily : button.dataset.v2Category === 'protein_powder' ? 'upsell' : 'oatmeal', true)));
    const onPopState = () => activateFamily(familyFromUrl());
    window.addEventListener('popstate', onPopState);
    all('.v2-purchase-options input[type=radio]').forEach(input => input.addEventListener('change', () => { mode = input.value; showError(''); update(); measure('purchase_type_changed'); }));
    all('[data-v2-frequency]').forEach(select => select.addEventListener('change', () => {
      explicitPlanChoice = true;
      selectedPlan = plans.find(plan => String(plan.intervalCount) === select.value);
      all('[data-v2-frequency]').forEach(other => { other.value = select.value; });
      showError('');
      update();
      measure('interval_changed');
    }));
    find('[data-v2-preview]').addEventListener('click', addToCart);
    find('[data-v2-jump]').addEventListener('click', () => {
      if (snapshot().count) { addToCart(); return; }
      const panel = find('[data-v2-flavor-panel]');
      panel.scrollIntoView({ behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth', block: 'start' });
      panel.querySelector('[role="tab"][aria-selected="true"]:not([data-v2-category])')?.focus({ preventScroll: true });
    });
    find('[data-v2-upsell-toggle]').addEventListener('click', () => {
      activateFamily('upsell', true);
      find('[data-v2-flavor-panel]').scrollIntoView({ behavior: 'instant', block: 'nearest' });
    });

    function initGallery(gallery) {
      if (gallery.dataset.initialized) return;
      gallery.dataset.initialized = 'true';
      const track = gallery.querySelector('[data-v2-slides]');
      if (!track) return;
      const slides = [...track.querySelectorAll(':scope > [data-v2-slide]')];
      if (!slides.length) return;
      const thumbnails = [...gallery.querySelectorAll('[data-v2-thumbnail]')];
      let index = 0;
      let measuredIndex = 0;
      function jump(next) { index = Math.max(0, Math.min(slides.length - 1, next)); track.scrollTo({ left: slides[index].offsetLeft - slides[0].offsetLeft, behavior: 'instant' }); sync(); }
      compactContent(gallery);
      function sync() {
        if (index !== measuredIndex) { measuredIndex = index; measure('gallery_changed', { family: gallery.dataset.v2Gallery, slide: index + 1 }); }
        thumbnails.forEach((thumb, i) => thumb.setAttribute('aria-pressed', String(i === index)));
        const thumbnailTrack = gallery.querySelector('.v2-thumbnails');
        const active = thumbnails[index];
        if (active) thumbnailTrack.scrollTo({ left: active.offsetLeft - thumbnails[0].offsetLeft - 60, behavior: 'instant' });
        slides.forEach((slide, i) => { if (i !== index) slide.querySelector('video')?.pause(); });
      }
      const arrows = [...gallery.querySelectorAll('[data-v2-gallery-step]')];
      arrows.forEach(button => button.addEventListener('click', () => jump((index + Number(button.dataset.v2GalleryStep) + slides.length) % slides.length)));
      arrows.forEach(button => { button.hidden = slides.length < 2; });
      thumbnails.forEach((thumb, i) => thumb.addEventListener('click', () => jump(i)));
      track.addEventListener('scroll', () => { if (!drag && track.clientWidth) { const next = Math.round(track.scrollLeft / track.clientWidth); if (next !== index) { index = Math.min(slides.length - 1, next); sync(); } } }, { passive: true });
      track.addEventListener('keydown', event => { if (event.target !== track) return; if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') { event.preventDefault(); jump(index + (event.key === 'ArrowRight' ? 1 : -1)); } });
      // Touch keeps native scrolling. Mouse uses pointer capture, including outside the track.
      let drag = null;
      track.addEventListener('dragstart', event => event.preventDefault());
      track.addEventListener('pointerdown', event => {
        if (event.pointerType !== 'mouse' || event.button !== 0 || event.target.closest('video')) return;
        drag = { id: event.pointerId, x: event.clientX, left: track.scrollLeft, index };
        track.classList.add('is-dragging');
        track.setPointerCapture(event.pointerId);
        event.preventDefault();
      });
      track.addEventListener('pointermove', event => {
        if (!drag || drag.id !== event.pointerId) return;
        track.scrollLeft = drag.left + drag.x - event.clientX;
      });
      function finishDrag(event) {
        if (!drag || drag.id !== event.pointerId) return;
        const delta = track.scrollLeft - drag.left;
        let next = Math.round(track.scrollLeft / track.clientWidth);
        if (next === drag.index && Math.abs(delta) > Math.min(50, track.clientWidth * .15)) next += Math.sign(delta);
        drag = null;
        jump(next);
        track.classList.remove('is-dragging');
        if (track.hasPointerCapture(event.pointerId)) track.releasePointerCapture(event.pointerId);
      }
      track.addEventListener('pointerup', finishDrag);
      track.addEventListener('pointercancel', finishDrag);
      track.addEventListener('lostpointercapture', finishDrag);
    }
    const sticky = find('[data-v2-sticky]');
    // Third-party bottom teasers need the actual bar height, including wrapped
    // captions and the device safe area, to keep the purchase button accessible.
    const stickySize = new ResizeObserver(() => {
      document.body.style.setProperty('--v2-sticky-clearance', `${Math.ceil(sticky.getBoundingClientRect().height)}px`);
    });
    stickySize.observe(sticky);
    const observer = new IntersectionObserver(entries => { sticky.hidden = entries[0].isIntersecting; }, { threshold: .1 });
    observer.observe(find('[data-v2-preview]'));
    root.v2Cleanup = () => { observer.disconnect(); stickySize.disconnect(); document.body.style.removeProperty('--v2-sticky-clearance'); window.removeEventListener('popstate', onPopState); };
    activateFamily(familyFromUrl());
    root.dataset.v2Mounted = 'true';
  }
  function init(scope = document) { scope.querySelectorAll('[data-v2-pdp]').forEach(mount); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(), { once: true }); else init();
  document.addEventListener('shopify:section:load', event => init(event.target));
  document.addEventListener('shopify:section:unload', event => event.target.querySelectorAll('[data-v2-pdp]').forEach(root => root.v2Cleanup?.()));
})();
